import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView, useSpring, useTransform, useScroll, useAnimation, AnimatePresence } from 'framer-motion';

// ============================================
// FADE IN WHEN VISIBLE
// Animación que aparece cuando el elemento entra en viewport
// ============================================
export const FadeInWhenVisible = ({
  children,
  delay = 0,
  duration = 0.6,
  direction = 'up', // 'up', 'down', 'left', 'right', 'none'
  distance = 40,
  className = '',
  once = true
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: "-50px" });

  const getInitialPosition = () => {
    switch (direction) {
      case 'up': return { y: distance, x: 0 };
      case 'down': return { y: -distance, x: 0 };
      case 'left': return { x: distance, y: 0 };
      case 'right': return { x: -distance, y: 0 };
      default: return { x: 0, y: 0 };
    }
  };

  const initial = { opacity: 0, ...getInitialPosition() };
  const animate = isInView ? { opacity: 1, x: 0, y: 0 } : initial;

  return (
    <motion.div
      ref={ref}
      initial={initial}
      animate={animate}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1] // Custom easing for smooth feel
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// STAGGER CONTAINER & ITEM
// Para animaciones escalonadas de múltiples elementos
// ============================================
export const StaggerContainer = ({
  children,
  staggerDelay = 0.1,
  delayStart = 0,
  className = ''
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: staggerDelay,
            delayChildren: delayStart
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({
  children,
  className = '',
  direction = 'up',
  distance = 30
}) => {
  const getInitialPosition = () => {
    switch (direction) {
      case 'up': return { y: distance };
      case 'down': return { y: -distance };
      case 'left': return { x: distance };
      case 'right': return { x: -distance };
      default: return {};
    }
  };

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, ...getInitialPosition() },
        visible: {
          opacity: 1,
          x: 0,
          y: 0,
          transition: {
            duration: 0.6,
            ease: [0.25, 0.1, 0.25, 1]
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// ANIMATED COUNTER
// Números que se incrementan animadamente
// ============================================
export const AnimatedCounter = ({
  value,
  duration = 2,
  suffix = '',
  prefix = '',
  className = ''
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [displayValue, setDisplayValue] = useState(0);

  // Parse the numeric value
  const numericValue = parseInt(value.toString().replace(/[^0-9]/g, ''), 10);

  useEffect(() => {
    if (isInView) {
      let startTime;
      let animationFrame;

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

        // Easing function for smooth deceleration
        const easeOut = 1 - Math.pow(1 - progress, 3);
        setDisplayValue(Math.floor(easeOut * numericValue));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [isInView, numericValue, duration]);

  return (
    <motion.span
      ref={ref}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5 }}
      className={className}
    >
      {prefix}{displayValue}{suffix}
    </motion.span>
  );
};

// ============================================
// PARALLAX SECTION
// Efecto parallax en scroll
// ============================================
export const ParallaxSection = ({
  children,
  speed = 0.5,
  className = ''
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, speed * 100]);

  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
};

// ============================================
// FLOATING ELEMENT
// Elemento que flota suavemente
// ============================================
export const FloatingElement = ({
  children,
  duration = 3,
  distance = 15,
  delay = 0,
  className = ''
}) => {
  return (
    <motion.div
      animate={{
        y: [-distance, distance, -distance],
      }}
      transition={{
        duration,
        repeat: Infinity,
        ease: "easeInOut",
        delay
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// SCALE ON HOVER
// Efecto de escala al pasar el ratón
// ============================================
export const ScaleOnHover = ({
  children,
  scale = 1.05,
  className = ''
}) => {
  return (
    <motion.div
      whileHover={{ scale }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// TEXT REVEAL
// Revela texto letra por letra o palabra por palabra
// ============================================
export const TextReveal = ({
  text,
  className = '',
  delay = 0,
  type = 'words' // 'words' or 'chars'
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const items = type === 'words' ? text.split(' ') : text.split('');
  const separator = type === 'words' ? '\u00A0' : '';

  return (
    <motion.span
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: type === 'words' ? 0.08 : 0.03,
            delayChildren: delay
          }
        }
      }}
      className={className}
    >
      {items.map((item, index) => (
        <motion.span
          key={index}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.4 }
            }
          }}
          style={{ display: 'inline-block' }}
        >
          {item}{separator}
        </motion.span>
      ))}
    </motion.span>
  );
};

// ============================================
// MAGNETIC BUTTON
// Botón con efecto magnético que sigue el cursor
// ============================================
export const MagneticButton = ({
  children,
  className = '',
  strength = 0.3
}) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouse = (e) => {
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const x = (clientX - (left + width / 2)) * strength;
    const y = (clientY - (top + height / 2)) * strength;
    setPosition({ x, y });
  };

  const reset = () => setPosition({ x: 0, y: 0 });

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={reset}
      animate={position}
      transition={{ type: "spring", stiffness: 150, damping: 15 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// REVEAL ON SCROLL
// Revelado con clip-path al hacer scroll
// ============================================
export const RevealOnScroll = ({
  children,
  className = '',
  direction = 'up' // 'up', 'down', 'left', 'right'
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const getClipPath = () => {
    switch (direction) {
      case 'up': return ['inset(100% 0 0 0)', 'inset(0 0 0 0)'];
      case 'down': return ['inset(0 0 100% 0)', 'inset(0 0 0 0)'];
      case 'left': return ['inset(0 100% 0 0)', 'inset(0 0 0 0)'];
      case 'right': return ['inset(0 0 0 100%)', 'inset(0 0 0 0)'];
      default: return ['inset(100% 0 0 0)', 'inset(0 0 0 0)'];
    }
  };

  const [initial, final] = getClipPath();

  return (
    <motion.div
      ref={ref}
      initial={{ clipPath: initial }}
      animate={isInView ? { clipPath: final } : {}}
      transition={{ duration: 0.8, ease: [0.77, 0, 0.175, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// GLOW EFFECT
// Efecto de brillo pulsante
// ============================================
export const GlowEffect = ({
  children,
  className = '',
  color = 'rgba(244, 143, 177, 0.4)'
}) => {
  return (
    <motion.div
      animate={{
        boxShadow: [
          `0 0 20px ${color}`,
          `0 0 40px ${color}`,
          `0 0 20px ${color}`
        ]
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// ANIMATED GRADIENT
// Gradiente animado de fondo
// ============================================
export const AnimatedGradient = ({
  className = '',
  colors = ['#f48fb1', '#d4a5a5', '#f48fb1']
}) => {
  return (
    <motion.div
      animate={{
        background: [
          `linear-gradient(45deg, ${colors[0]}, ${colors[1]})`,
          `linear-gradient(45deg, ${colors[1]}, ${colors[2]})`,
          `linear-gradient(45deg, ${colors[2]}, ${colors[0]})`,
          `linear-gradient(45deg, ${colors[0]}, ${colors[1]})`
        ]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "linear"
      }}
      className={className}
    />
  );
};

// ============================================
// SPRING MODAL / DRAWER
// Modal con animación spring
// ============================================
export const SpringModal = ({
  isOpen,
  onClose,
  children,
  className = ''
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={`fixed z-50 ${className}`}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Export motion for direct use
export { motion, AnimatePresence, useInView, useScroll, useTransform };
